/**
 * 文本图层栅格化（M2-18 / DESIGN.md §2.5「自己转成路径渲染」）。
 *
 * 排版（M2-17）给出 glyph id + 位置，这里取轮廓（SVG path）画到 2D canvas，
 * 再按块写进 TileStore——TextLayer 本身不持有像素（规则 6），
 * 像素以图层 id 存在 store 里，与 raster 图层同一套通道。
 *
 * 重栅格化是**替换语义**：先清掉旧块再写新块（与变换烘焙一致），
 * 整段进一个像素事务，undo 逐位还原。
 */
import { TILE, tileKey, type LayerId } from '../../engine/tile/key';
import { BYTES_PER_TILE, type TileStore } from '../../engine/tile/store';
import type { StrokeTransaction } from '../../engine/brush/buffer';
import { layoutTextSpans, type LayoutOptions, type StyledSpan } from '../../engine/text/layout';
import { layoutVertical, rotationMatrix, type VerticalGlyph } from '../../engine/text/vertical';
import { sampleWarp, type WarpParams } from '../../engine/text/warp';
import type { Shaper } from '../../engine/text/shaper';
import type { VectorFill } from '../../document/types';

/**
 * 一段用同一套样式排版的文字（富文本混合格式，M4-38：用户反馈"选中部分
 * 文字改字体/颜色"）——对应 `TextLayer.runs` 数组里的一条 `TextRun`，
 * `shaper` 是调用方已经解析好的这个 `fontFamily` 对应的 shaper。
 */
export interface TextSpecRun {
  readonly text: string;
  readonly shaper: Shaper;
  readonly fontSize: number;
  readonly fill: VectorFill;
  readonly stroke?: { readonly fill: VectorFill; readonly width: number };
}

export interface TextSpec {
  /** 至少一条；多条时按顺序拼接成完整文字，各自用自己的字体/字号/颜色。 */
  readonly runs: readonly TextSpecRun[];
  readonly lineHeight: number;
  /** 段落文本的容器宽；点文本为 0。横排用；竖排时容器方向变成高。 */
  readonly boxWidth: number;
  /**
   * 横排（默认）或竖排（M3-16）。竖排不支持混合格式——多条 run 会拼接
   * 全部文字内容，但统一用第一条 run 的字体/字号/颜色渲染（已披露的
   * 简化，见 M4-38 PROGRESS 记录）。
   */
  readonly writingMode?: 'horizontal' | 'vertical';
  /** 文字变形（阶段 5）。只作用于横排——竖排文字变形不在这次范围内。 */
  readonly warp?: WarpParams;
}

/** 把矢量取色应用成 canvas 的 fillStyle（渐变用图层自身宽度，从左到右）。 */
function toCanvasStyle(
  ctx: CanvasRenderingContext2D,
  fill: VectorFill,
  width: number,
): string | CanvasGradient {
  if (fill.type === 'solid') return fill.color;
  const gradient = ctx.createLinearGradient(0, 0, width, 0);
  for (const stop of fill.stops) gradient.addColorStop(Math.min(1, Math.max(0, stop.offset)), stop.color);
  return gradient;
}

/**
 * 画一层字形轮廓（全部实心黑）再用 source-in 把整片剪影重新上色——
 * 直接把渐变/纯色设成 fillStyle 在逐字形的局部变换（每个字形有自己的
 * translate+scale）里画，会让渐变跟着每个字形的局部坐标系重复摆一遍，
 * 变成一堆小渐变而不是整行字一条渐变。source-in 这一步发生在外层
 * （没有任何局部变换），颜色因此稳定覆盖整个画布范围，跟字形怎么摆
 * 无关——这是 canvas 里给任意路径集合上渐变色的标准写法。
 */
function buildLayer(
  width: number,
  height: number,
  paint: (ctx: CanvasRenderingContext2D) => void,
  fill: VectorFill,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('无法创建文本栅格化画布。');
  ctx.fillStyle = '#000';
  ctx.strokeStyle = '#000';
  paint(ctx);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = toCanvasStyle(ctx, fill, width);
  // source-in 会把结果 alpha 收窄到 min(剪影 alpha, 这次填色的 alpha)——
  // 纯色的不透明度就是这样叠上去的，渐变目前恒为 1（色标自己的不透明度
  // 是渐变编辑器里独立的滑杆，跟这里不是一回事，暂不打通）
  ctx.globalAlpha = fill.type === 'solid' ? (fill.alpha ?? 1) : 1;
  ctx.fillRect(0, 0, width, height);
  ctx.globalAlpha = 1;
  return canvas;
}

export interface RasterizeResult {
  /** 文档空间的内容外接矩形。 */
  readonly bounds: { x: number; y: number; width: number; height: number };
  readonly dirty: ReadonlyArray<{ tx: number; ty: number }>;
}

/**
 * 画一个竖排 glyph。rotate=90 的（拉丁/数字串）先顺时针转 90° 再按
 * 字体坐标系（y 向上）翻转——旋转后原本的"上"变成"右"，视觉上就是
 * 整串拉丁字母横过来朝右读，符合竖排排版里拉丁文倒向的惯例。
 */
function drawVerticalGlyph(
  ctx: CanvasRenderingContext2D,
  shaper: Shaper,
  glyph: VerticalGlyph,
  scale: number,
  originX: number,
  mode: 'fill' | 'stroke',
): void {
  const path = shaper.glyphPath(glyph.glyphId);
  if (path === '') return;
  ctx.save();
  ctx.translate(originX + glyph.x, glyph.y);
  const [a, b, c, d] = rotationMatrix(glyph.rotate);
  ctx.transform(a, b, c, d, 0, 0);
  ctx.scale(scale, -scale);
  const path2d = new Path2D(path);
  if (mode === 'fill') ctx.fill(path2d);
  else ctx.stroke(path2d);
  ctx.restore();
}

/** 把文本栅格化到图层（原点 origin 为文本框左上角，文档空间）。 */
export function rasterizeText(
  store: TileStore,
  layer: LayerId,
  spec: TextSpec,
  origin: { x: number; y: number },
  transaction: StrokeTransaction,
): RasterizeResult {
  const vertical = spec.writingMode === 'vertical';
  const firstRun = spec.runs[0];
  if (firstRun === undefined) {
    throw new Error('rasterizeText 至少需要一条 run（文本框不能没有内容）。');
  }

  let width: number;
  let height: number;
  /** 只画属于某一条 run 的字形（竖排不区分 run，恒画全部）——每条 run
   * 自己的字体/字号各自换算 scale，混排字号不同的字形因此各自正确缩放。 */
  let drawGlyphsForRun: (ctx: CanvasRenderingContext2D, mode: 'fill' | 'stroke', runIndex: number) => void;

  if (vertical) {
    // 竖排不支持混合格式（M4-38 已披露的简化）：拼接全部 run 的文字内容，
    // 但统一用第一条 run 的字体/字号渲染，跟"选中部分文字改样式"这个功能
    // 无关——用户目前也没有 UI 入口能对竖排文字做局部选样式
    const text = spec.runs.map((r) => r.text).join('');
    const shaper = firstRun.shaper;
    const scale = firstRun.fontSize / shaper.upem;
    const layout = layoutVertical(shaper, text, {
      fontSize: firstRun.fontSize,
      lineHeight: spec.lineHeight,
      ...(spec.boxWidth > 0 ? { maxHeight: spec.boxWidth } : {}),
    });
    // 竖排的 x 是「行中心线相对最右行」的偏移（≤0）；画布原点需要挪到最左行
    width = Math.max(1, Math.ceil(layout.width) + firstRun.fontSize);
    height = Math.max(1, Math.ceil(spec.boxWidth > 0 ? spec.boxWidth : layout.height) + 2);
    const originX = width - firstRun.fontSize / 2; // 最右行的中心线在画布里的 x
    drawGlyphsForRun = (ctx, mode) => {
      for (const glyph of layout.glyphs) drawVerticalGlyph(ctx, shaper, glyph, scale, originX, mode);
    };
  } else {
    const spans: StyledSpan[] = spec.runs.map((r) => ({
      text: r.text,
      shaper: r.shaper,
      fontSize: r.fontSize,
    }));
    const options: LayoutOptions = {
      fontSize: Math.max(...spec.runs.map((r) => r.fontSize)),
      lineHeight: spec.lineHeight,
      ...(spec.boxWidth > 0 ? { maxWidth: spec.boxWidth } : {}),
    };
    const layout = layoutTextSpans(spans, options);
    const warp = spec.warp;
    // 变形会把字形挪到行范围之外（弯曲的顶点会探出原本的矩形），画布要
    // 留出振幅的余量，否则弯曲的字形会被裁掉——振幅本身按字号缩放
    // （见 warp.ts 的 amplitude 参数），跟横排/竖排布局的其余尺寸算法
    // 用同一个量级的余量（+2px）不够，这里换成振幅本身
    const warpAmplitude = options.fontSize * 1.5;
    const warpMargin = warp === undefined ? 0 : Math.ceil(warpAmplitude);
    width = Math.max(1, Math.ceil(spec.boxWidth > 0 ? spec.boxWidth : layout.width) + 2);
    height = Math.max(1, Math.ceil(layout.height) + 2 + warpMargin * 2);
    const warpOriginY = warpMargin; // 变形余量顶在上方，字形整体下移这么多
    drawGlyphsForRun = (ctx, mode, runIndex) => {
      const run = spec.runs[runIndex]!;
      const scale = run.fontSize / run.shaper.upem;
      for (const glyph of layout.glyphs) {
        if (glyph.spanIndex !== runIndex) continue;
        const path = run.shaper.glyphPath(glyph.glyphId);
        if (path === '') continue;
        ctx.save();
        if (warp === undefined) {
          // 字体坐标系 y 向上 → 画布 y 向下
          ctx.translate(glyph.x, glyph.y + warpOriginY);
          ctx.scale(scale, -scale);
        } else {
          const t = layout.width > 0 ? glyph.x / layout.width : 0;
          const sample = sampleWarp(warp, t, layout.width, warpAmplitude);
          ctx.translate(glyph.x, glyph.y + sample.dy + warpOriginY);
          const [a, b, c, d] = rotationMatrix(sample.angle);
          ctx.transform(a, b, c, d, 0, 0);
          ctx.scale(scale, -scale);
        }
        const path2d = new Path2D(path);
        if (mode === 'fill') ctx.fill(path2d);
        else ctx.stroke(path2d);
        ctx.restore();
      }
    };
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('无法创建文本栅格化画布。');

  // 逐条 run 画（描边先画、填充再盖上去——跟真实描边文字的呈现方式
  // 一致），而不是整段文字画一遍：每条 run 可能字体/字号/颜色都不同，
  // "剪影再统一上色"（buildLayer 的 source-in 手法）没法给不同字形上
  // 不同颜色，必须分开各画各的层再依次合成
  spec.runs.forEach((run, runIndex) => {
    if (run.stroke !== undefined && run.stroke.width > 0) {
      const scale = run.fontSize / run.shaper.upem;
      const strokeLayer = buildLayer(
        width,
        height,
        (c) => {
          c.lineWidth = run.stroke!.width / scale;
          drawGlyphsForRun(c, 'stroke', runIndex);
        },
        run.stroke.fill,
      );
      ctx.drawImage(strokeLayer, 0, 0);
    }
    const fillLayer = buildLayer(width, height, (c) => drawGlyphsForRun(c, 'fill', runIndex), run.fill);
    ctx.drawImage(fillLayer, 0, 0);
  });
  const image = ctx.getImageData(0, 0, width, height);

  // 旧块 ∪ 新块 → touch → 快照 → 清旧写新
  const dirty = new Map<string, { tx: number; ty: number }>();
  for (const tile of store.residentTilesOf(layer, 0)) {
    dirty.set(`${tile.key.tx} ${tile.key.ty}`, { tx: tile.key.tx, ty: tile.key.ty });
  }
  const tx0 = Math.max(0, Math.floor(origin.x / TILE));
  const ty0 = Math.max(0, Math.floor(origin.y / TILE));
  const tx1 = Math.floor((origin.x + width - 1) / TILE);
  const ty1 = Math.floor((origin.y + height - 1) / TILE);
  for (let ty = ty0; ty <= ty1; ty++) {
    for (let tx = tx0; tx <= tx1; tx++) dirty.set(`${tx} ${ty}`, { tx, ty });
  }
  for (const { tx, ty } of dirty.values()) transaction.touch(tileKey(layer, tx, ty));
  transaction.capture();

  for (const { tx, ty } of dirty.values()) {
    const key = tileKey(layer, tx, ty);
    const out = new Uint8Array(BYTES_PER_TILE);
    let any = false;
    const left = Math.max(Math.floor(origin.x), tx * TILE);
    const top = Math.max(Math.floor(origin.y), ty * TILE);
    const right = Math.min(Math.floor(origin.x) + width, (tx + 1) * TILE);
    const bottom = Math.min(Math.floor(origin.y) + height, (ty + 1) * TILE);
    for (let y = top; y < bottom; y++) {
      for (let x = left; x < right; x++) {
        const from = ((y - Math.floor(origin.y)) * width + (x - Math.floor(origin.x))) * 4;
        if (image.data[from + 3] === 0) continue;
        const to = ((y % TILE) * TILE + (x % TILE)) * 4;
        out[to] = image.data[from]!;
        out[to + 1] = image.data[from + 1]!;
        out[to + 2] = image.data[from + 2]!;
        out[to + 3] = image.data[from + 3]!;
        any = true;
      }
    }
    if (any) store.set(key, out);
    else store.clear(key);
  }
  return {
    bounds: { x: Math.floor(origin.x), y: Math.floor(origin.y), width, height },
    dirty: [...dirty.values()],
  };
}
